import { useState } from "react"
import { Typography } from "@mui/material"
import { Form, Formik, FormikHelpers } from "formik"
import { makeStyles } from "tss-react/mui"
import * as Yup from "yup"

import { StandardDialog } from "../../../../../../../../shared/components/dialogs/StandardDialog"
import { FormikTextField } from "../../../../../../../../shared/components/forms/Formik"
import { ImageUploadDropzone } from "../../../../../../../../shared/components/Formik/ImageUploadDropzone"
import { StandardLoadingButton } from "../../../../../../../../shared/components/buttons/LoadingButton"
import { useApiMutation } from "../../../../../../../../core/api/useApiMutation"
import { uploadImage } from "../../../../../../../entities/images/imagesApi"
import { LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../../localization/components/AppLocalizationProvider"

export type CreatedTableArrangement = Readonly<{
    label: string
    imageSrc: string
    imageUid: string
}>

type CreateTableArrangementDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    onCreated: (arrangement: CreatedTableArrangement) => void
}>

type FormValues = {
    name: string
}

const IMAGE_ENTITY_TYPE = "table_arrangement"

const useStyles = makeStyles()(() => ({
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
        padding: "1rem 4rem 1.5rem",
    },
    imageField: {
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
    },
    imageLabel: {
        fontWeight: 500,
    },
    submitButton: {
        alignSelf: "center",
        minWidth: "12rem",
        marginTop: "1rem",
    },
}))

export function CreateTableArrangementDialog({ open, onClose, onCreated }: CreateTableArrangementDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const [imageFile, setImageFile] = useState<File | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string>("")
    const [imageError, setImageError] = useState<string>()

    const { mutateAsync: callUploadImage } = useApiMutation(uploadImage, {})

    const validationSchema = Yup.object({
        name: Yup.string().trim().required(l10n.getString("tab-table-arrangements-create-dialog-name-required-error")),
    })

    const handleImageChange = (file: File | null) => {
        setImageError(undefined)
        if (file) {
            setImageFile(file)
            setPreviewUrl(URL.createObjectURL(file))
        } else {
            setImageFile(null)
            setPreviewUrl("")
        }
    }

    const resetState = () => {
        setImageFile(null)
        setPreviewUrl("")
        setImageError(undefined)
    }

    const handleClose = () => {
        resetState()
        onClose()
    }

    const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
        if (!imageFile) {
            setImageError(l10n.getString("tab-table-arrangements-create-dialog-image-required-error"))
            return
        }

        // The image upload endpoint exists, so this is a real upload and gives us a uid.
        const { image } = await callUploadImage({ image: imageFile, typeOfEntity: IMAGE_ENTITY_TYPE })

        // TODO: once the backend is ready, persist the arrangement via
        // `createTableArrangement({ name, imageUid })` (see tableArrangementsApi.ts) and use the
        // returned arrangement instead of keeping it in the parent's local state.
        onCreated({ label: values.name.trim(), imageSrc: previewUrl, imageUid: image.uid })

        helpers.resetForm()
        handleClose()
    }

    return (
        <StandardDialog
            open={open}
            onClose={handleClose}
            dialogTitle={l10n.getString("tab-table-arrangements-create-dialog-title")}
        >
            <Formik<FormValues>
                initialValues={{ name: "" }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting }) => (
                    <Form className={classes.form}>
                        <LocalizedStrict id="tab-table-arrangements-create-dialog-name-label" attrs={{ label: true }}>
                            <FormikTextField<FormValues> name="name" label="" fullWidth />
                        </LocalizedStrict>

                        <div className={classes.imageField}>
                            <LocalizedStrict id="tab-table-arrangements-create-dialog-image-label">
                                <Typography className={classes.imageLabel}>Image</Typography>
                            </LocalizedStrict>
                            <ImageUploadDropzone onImageChange={handleImageChange} />
                            {imageError !== undefined && (
                                <Typography variant="body2" color="error">
                                    {imageError}
                                </Typography>
                            )}
                        </div>

                        <LocalizedStrict id="tab-table-arrangements-create-dialog-submit-button">
                            <StandardLoadingButton
                                type="submit"
                                loading={isSubmitting}
                                fullWidth={false}
                                className={classes.submitButton}
                            >
                                Create
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    </Form>
                )}
            </Formik>
        </StandardDialog>
    )
}
